/**
 * CJAS — donations.  Jira CA-76, CA-78, built on ADR-002 (CA-75, CA-87).
 *
 * The platform does NOT process payments. Solidus is the money rail and files
 * the gaveinnberetning; Vipps is where the member actually pays. This module
 * models the flow around that: where the Donate button goes, what happens
 * when the member comes back, and what the app is allowed to claim.
 *
 * The rule Apple imposes and this file encodes: unless CJAS is an approved
 * nonprofit, funds are collected OUTSIDE the app. So the flow leaves and
 * returns, and the return is the interesting part — because a member who
 * pays and then sees "ingen gaver registrert" concludes the payment failed
 * and pays again.
 */

const PROVIDERS = ['vipps', 'solidus_web'];

/**
 * Payment methods (CA-77).
 *
 * Vipps is what almost everyone in the congregation already has. Card is the
 * fallback for the ones who do not — a member who has just moved to Norway, an
 * older member with no smartphone banking, a visitor giving at Eid — and it is
 * also the path a browser donation from cjas.no takes.
 *
 * Both are collected OUTSIDE the app, on the Solidus-hosted page. Changing the
 * instrument does not change the Apple ruling in ADR-002: the rule is about
 * where the money is collected, not what it is collected with. So adding card
 * does not buy us an in-app payment sheet, and this module must not pretend it
 * does.
 *
 * No card number, expiry, CVC or PAN fragment ever reaches this platform. The
 * member types it on the acquirer's page. We hold a reference and an amount —
 * the same fields as for Vipps — and that is all.
 */
const METHODS = ['vipps', 'card', 'bank_transfer'];

/**
 * Bank transfer (CA-87, decided by CJAS 2026-09-09).
 *
 * *"it will be subscription based on vipps, one time on vipps and transfer to
 * bank."* — so a plain bank transfer is a first-class way to give, not a
 * footnote. It is also the only one with no redirect: the member types the
 * account number into their own bank, and nothing comes back to the app.
 *
 * That changes what we are allowed to say more than the other two. With Vipps
 * we at least know the member reached the payment page; with a bank transfer
 * we do not even know they opened their bank. So there is no "thank you"
 * screen pretending otherwise, and no return state to interpret.
 */

/**
 * CJAS declined the approved-nonprofit route on CA-87: *"we will not take
 * donation through apple pay and google pay."*
 *
 * That closes the Benevity application, and with it the only path to an
 * in-app payment sheet on iOS (Apple 3.2.1(vi) requires Apple Pay support as
 * a condition of fundraising in-app). Every method therefore collects money
 * OUTSIDE the app, on both platforms, permanently — not as a temporary
 * position while an approval is pending.
 *
 * The `approvedNonprofit` branch below is kept and still tested, because it
 * documents what the rule actually is and what would change if CJAS ever
 * reversed the decision. Nothing in the product passes `true`.
 */
const APPROVED_NONPROFIT = false;

/**
 * Card is not offered at launch (CA-77, decided by Daniyal 2026-09-18).
 *
 * CJAS named three ways to give on CA-87 — Vipps monthly, Vipps one-off and
 * bank transfer — and card was not one of them. Offering it would also need a
 * card payment page to send people to (Solidus, Nets or Stripe), which does
 * not exist. So the product offers Vipps and bank transfer, and bank transfer
 * is the way for a member without Vipps.
 *
 * This is different from `cardEnabled: false`. That switch means the card rail
 * EXISTS but is down for a while, and the screen says so ("midlertidig
 * utilgjengelig"). Not offered means there is nothing to apologise for: card
 * is simply not listed, and no note is shown. Everything card does is kept and
 * still tested, so turning it on later is this one line plus a payment page.
 */
const CARD_OFFERED = false;
/*
 * Fitrana and langar became gift purposes of their own (owner 2026-10-29): until then a fitrana or langar campaign gave
 * as «general». Validated here in code only — `donation.purpose` and `donation_agreement.purpose` carry no CHECK, so no
 * migration. (The cash-gift ledger's `cash_gift.purpose` DOES carry a CHECK — of exactly this list, since 066 was
 * widened before it was final: cash-gifts.js uses this list, and a purpose added here needs that CHECK widened too.)
 */
const PURPOSES = ['general', 'building', 'zakat', 'sadaqah', 'qurbani', 'fitrana', 'langar'];
/** The purposes' Norwegian names (the donate screen, the panel's gift list, receipts' «Formål»). */
const PURPOSE_NO = { general: 'Der det trengs mest', building: 'Byggefond', zakat: 'Zakat', sadaqah: 'Sadaqah', qurbani: 'Qurbani',
  fitrana: 'Fitrana', langar: 'Langar' };

/** Amounts that fit on a phone screen as tappable chips, in NOK. */
const SUGGESTED = [100, 200, 500, 1000];
const MIN_AMOUNT = 20;
const MAX_AMOUNT = 100000;

/**
 * Whether the Donate button may open a payment sheet inside the app, or must
 * leave it. See ADR-002 — this is the Apple rule, not a preference.
 */
function presentation(platform, approvedNonprofit = false) {
  if (platform === 'android') {
    return { mode: 'external', reason: 'policy_safe',
      note: 'Play allows an external donation flow; donations buy no digital content.' };
  }
  if (approvedNonprofit) {
    return { mode: 'in_app', requiresApplePay: true,
      reason: 'apple_3_2_1_vi',
      note: 'Approved nonprofits may fundraise in-app provided Apple Pay is offered.' };
  }
  return { mode: 'external', reason: 'apple_3_2_2_iv',
    note: 'Not an approved nonprofit: funds may only be collected outside the app.' };
}

/**
 * Which methods the donate screen offers, in the order they are shown.
 *
 * Vipps first because it is one tap for most of the congregation; card second
 * and always visible, not hidden behind "andre betalingsmåter". A member who
 * does not have Vipps should not have to hunt for the way that works for them,
 * and someone who has been told "you need Vipps" once tends not to come back.
 *
 * The mosque pays more per card donation than per Vipps one. That is the
 * mosque's cost, not the member's problem: nothing here nudges away from card,
 * and there is deliberately no "Vipps er billigere for moskeen" line. Asking a
 * congregation to feel guilty about how they gave is not something this app does.
 */
function methodsFor(opts = {}) {
  const { cardOffered = CARD_OFFERED, cardEnabled = true, lang = 'no' } = opts;
  const labels = {
    vipps: { label_no: 'Vipps', hint_no: 'Raskest hvis du har Vipps.' },
    card:  { label_no: 'Bankkort', hint_no: 'Visa eller Mastercard. Du trenger ikke Vipps.' },
    bank_transfer: { label_no: 'Bankoverføring',
                     hint_no: 'Du overfører selv i nettbanken. Ingen app kreves.' },
  };
  const list = METHODS
    .filter(m => (m === 'card' ? (cardOffered && cardEnabled) : true))
    .map(m => ({ id: m, ...labels[m] }));
  return {
    methods: list,
    // Nothing is pre-selected, for the same reason no amount is: a default is
    // a recommendation, and we are not recommending how someone gives.
    preselected: null,
    lang,
    // If the card rail is offered but switched off, say why rather than
    // silently showing fewer options. When card is not offered at all there is
    // nothing to explain, and "midlertidig" would be untrue (CA-77).
    unavailableNote_no: (!cardOffered || cardEnabled) ? null
      : 'Kortbetaling er midlertidig utilgjengelig. Du kan gi kontant i moskeen eller be om kontonummer.',
  };
}

/*
 * Audit F-15 (2026-10-19): `Number('')` and `Number(null)` are 0, so an empty field said «Minste beløp er 20 kr»
 * instead of «Skriv et beløp»; and Number() also read '0x64', '1e3' and [100] as amounts. An amount is a number, or a
 * string of digits (with an optional decimal part, refused below as not whole kroner) — nothing else.
 */
function validateAmount(amount) {
  // Audit SPAY-02 (merged with F-15): the typed field may be a digit string; "0x1F4", [500], "1e3" are refused.
  // A START (below) takes a number only — the app sends the checked number, never the field.
  const given = typeof amount === 'number' || (typeof amount === 'string' && /^\s*\d+(\.\d+)?\s*$/.test(amount));
  const n = given ? Number(amount) : NaN;
  // `code` (+ min/max) beside the Norwegian sentence (Masjid UX redesign, MOB-04): the app says it in the member's own
  // language; `message_no` stays for everything that already reads it.
  if (!Number.isFinite(n)) return { ok: false, code: 'amount_missing', message_no: 'Skriv et beløp.' };
  if (n < MIN_AMOUNT) return { ok: false, code: 'amount_min', min: MIN_AMOUNT, message_no: `Minste beløp er ${MIN_AMOUNT} kr.` };
  if (n > MAX_AMOUNT) return { ok: false, code: 'amount_max', max: MAX_AMOUNT, message_no: `Ta kontakt med moskeen for gaver over ${MAX_AMOUNT} kr.` };
  if (!Number.isInteger(n)) return { ok: false, code: 'amount_whole', message_no: 'Skriv et helt kronebeløp.' };
  return { ok: true, amount: n };
}

/**
 * Start a donation.
 *
 * `reference` is generated here and carried out and back, so a returning
 * member can be matched to what they started even though the money moved
 * somewhere we cannot see. It is not a payment id — we never have one.
 */
/** A reference prefix: A–Z and 0–9, at most 8 characters (Vipps allows 8–50 of [a-zA-Z0-9-] in all). */
function cleanPrefix(p) {
  const c = String(p || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
  return c.length >= 2 ? c : 'GAVE';
}

/**
 * A mosque's own reference prefix (OA-015): its platform short address
 * ('cjas' → 'CJAS', so CJAS's references look as they always have), else its
 * short name, else 'GAVE'.
 */
function referencePrefix({ slug = null, shortName = null, override = null } = {}) {
  // OA-066: the operator's explicit choice (MASJID_DONATION_REF_PREFIX) first; unset keeps the rule below.
  if (override && cleanPrefix(override) !== 'GAVE') return cleanPrefix(override);
  if (slug && cleanPrefix(slug) !== 'GAVE') return cleanPrefix(slug);
  if (shortName && cleanPrefix(shortName) !== 'GAVE') return cleanPrefix(shortName);
  return 'GAVE';
}

const REFERENCE_SPACE = 36 ** 6;
function start(input = {}, opts = {}) {
  const { amount, purpose = 'general', recurring = false, method = 'vipps' } = input;
  // OA-015: the reference prefix and the payment page are the MOSQUE's. They were 'CJAS-' and
  // cjas.no/gi for every mosque. No page configured, no link (the app only opens a link that exists).
  // `randomInt(max)` → a whole number 0 ≤ n < max from a CRYPTOGRAPHIC source (the server passes node:crypto's
  // randomInt; QA 2026-10-16). Without it — the app, which has no node:crypto — `random` (Math.random) as before.
  const { platform = 'ios', approvedNonprofit = false, now = new Date(),
          baseUrl = null, random = Math.random, randomInt = null, prefix = 'GAVE' } = opts;

  // Audit SPAY-02: the request body's amount is a number, never coerced from a string or a list.
  const v = typeof amount === 'number' ? validateAmount(amount) : { ok: false, message_no: 'Skriv et beløp.' };
  if (!v.ok) return { ok: false, errors: { amount: v.message_no } };
  if (!PURPOSES.includes(purpose)) return { ok: false, errors: { purpose: 'Ukjent formål.' } };
  if (!METHODS.includes(method)) return { ok: false, errors: { method: 'Ukjent betalingsmåte.' } };
  if (recurring && !recurringSupported(method)) {
    return { ok: false, errors: { recurring: recurringSupported.reason_no(method) } };
  }

  // The same six base-36 characters as always (REFERENCE_SPACE = 36^6 with randomInt, 1e8 before): every reference
  // reader keeps working. Unique per mosque by the database; a collision is retried by the caller, never a 500.
  const n = typeof randomInt === 'function' ? randomInt(REFERENCE_SPACE) : Math.floor(random() * 1e8);
  const ref = cleanPrefix(prefix) + '-' + now.getFullYear() +
    '-' + n.toString(36).toUpperCase().padStart(6, '0');

  const present = presentation(platform, approvedNonprofit);

  /* A bank transfer has no payment page to send anybody to. Returning a URL
     for it — even a page explaining the transfer — would put it through the
     same "you are leaving, come back after" flow as Vipps, and a member who
     comes back to a thank-you screen having done nothing in their bank is
     being told something that is not true. It returns instructions instead,
     and the caller shows them in place. */
  if (method === 'bank_transfer') {
    return {
      ok: true,
      reference: ref,
      amount: v.amount,
      purpose,
      recurring,
      method,
      presentation: { mode: 'instructions', reason: 'no_redirect',
                      note: 'A bank transfer happens in the member\'s own bank.' },
      url: null,
      leaving_no: null,
      instructions_no: 'Overfør beløpet i nettbanken din, og skriv referansen i meldingsfeltet. '
        + 'Da finner moskeen igjen gaven din når kontoutskriften stemmes av.',
      // NEVER invent an account number. The caller supplies the mosque's real
      // one; when it is missing the screen must say "ask the mosque" rather
      // than print a plausible-looking string of digits, which is how somebody
      // sends money to nobody.
      accountNumber: opts.accountNumber || null,
      missingAccountNote_no: opts.accountNumber ? null
        : 'Kontonummeret er ikke lagt inn ennå. Spør i moskeen, eller bruk Vipps.',
      startedAt: now.toISOString(),
    };
  }

  return {
    ok: true,
    reference: ref,
    amount: v.amount,
    purpose,
    recurring,
    method,
    presentation: present,
    url: baseUrl ? `${baseUrl}?ref=${encodeURIComponent(ref)}&belop=${v.amount}&formal=${purpose}` +
         `&metode=${method}${recurring ? '&fast=1' : ''}` : null,
    // Said before the member leaves, so returning to an app that knows
    // nothing is not a surprise. Naming the actual destination matters: a
    // member who was told "Vipps" and lands on a bank card page assumes the
    // link is wrong and abandons it.
    leaving_no: present.mode === 'external'
      ? (method === 'card'
          ? 'Du sendes til en sikker betalingsside for å taste kortet. Kom tilbake til appen etterpå.'
          : 'Du sendes til Vipps for å fullføre gaven. Kom tilbake til appen etterpå.')
      : null,
    startedAt: now.toISOString(),
  };
}

/**
 * The member is back. This is where donations go wrong.
 *
 * We do not know whether the payment succeeded — Vipps told Solidus, not us,
 * and the import is weekly (CA-72). So the app must NOT claim success, must
 * NOT claim failure, and must not show "ingen gaver" as though nothing
 * happened. It says what is actually true: we will not see it for a few days.
 */
function onReturn(pending, now = new Date()) {
  if (!pending) return { state: 'unknown' };

  /* There is no "return" from a bank transfer — the member never left. And
     the money may take days to arrive and weeks to reach us through the
     import, so this is the one method where even "we will see it shortly" is
     more than we know. The honest state is: here is the reference, keep it. */
  if (pending.method === 'bank_transfer') {
    return {
      state: 'awaiting_transfer',
      method: 'bank_transfer',
      reference: pending.reference,
      amount: pending.amount,
      title_no: 'Referansen din',
      body_no: 'Skriv referansen i meldingsfeltet når du overfører. '
        // Said as a condition (QA 2026-10-16, A10): the reference is not stored anywhere, so the gift appears only once
        // the mosque has recorded the transfer — never as though it were already on its way into the history.
        + 'Gaven vises i giverhistorikken din når moskeen har lagt inn overføringen.',
      note_no: 'Ingen kvittering sendes herfra — den ligger i nettbanken din.',
      showRetry: false,
      keepLocallyUntilDays: 30,
    };
  }

  const method = pending.method === 'card' ? 'card' : 'vipps';
  const minutes = (now - new Date(pending.startedAt)) / 60000;

  /* Came back within seconds without paying — almost always a cancel.
     The window differs by method, and getting this wrong is not cosmetic.
     Vipps is one confirm in an app the member is already signed in to, so a
     genuine payment takes ten seconds and a return inside half a minute means
     they backed out. A card payment means typing a 16-digit number, then very
     often a 3-D Secure step that hands off to the bank's own app and back.
     Half a minute is a perfectly normal *successful* card payment, and telling
     that member "Gaven ble ikke fullført" would be a flat lie that makes them
     pay twice. So card gets no cancel window at all below: if a card donor
     comes back at all, we say we do not know yet, which is the truth. */
  const cancelWindowMinutes = method === 'card' ? 0 : 0.5;
  if (minutes < cancelWindowMinutes) {
    return { state: 'probably_cancelled',
      method,
      title_no: 'Gaven ble ikke fullført',
      body_no: 'Du kan prøve igjen når du vil.',
      showRetry: true };
  }

  /* Everything below is deliberately identical for both methods. A card gift
     and a Vipps gift reach the mosque through Solidus and appear in the same
     giving history on the same weekly import (CA-72), so the app must not
     imply that one is more or less confirmed than the other. Only the receipt
     sentence differs, because only Vipps keeps a receipt the member can open. */
  return {
    state: 'pending_confirmation',
    method,
    reference: pending.reference,
    amount: pending.amount,
    title_no: 'Takk!',
    // Never "Gaven er registrert" — we do not know that.
    body_no: method === 'card'
      ? 'Hvis betalingen gikk gjennom, er gaven mottatt. Den vises i giverhistorikken din om noen dager, når medlemslisten oppdateres.'
      : 'Hvis gaven ble fullført i Vipps, er den mottatt. Den vises i giverhistorikken din om noen dager, når medlemslisten oppdateres.',
    note_no: receiptNote(method),
    showRetry: false,
    // The app keeps this locally so the giving screen can say "a gift you
    // started on 6 September is not showing yet" rather than nothing.
    keepLocallyUntilDays: 14,
  };
}

/**
 * Where the member's receipt is. Vipps keeps one in the Vipps app; a card
 * payment does not, so the receipt is emailed by the payment page and the app
 * must say so — otherwise a card donor looks for a receipt that was never
 * going to be in Vipps, and concludes the payment failed.
 */
function receiptNote(method) {
  if (method === 'card')
    return 'Kvitteringen sendes på e-post fra betalingssiden. Sjekk også søppelpost.';
  // Nothing is emailed for a bank transfer, and pretending otherwise sends
  // somebody looking through their spam folder for a receipt that was never
  // created. Their own bank holds the only record.
  if (method === 'bank_transfer')
    return 'Kvitteringen ligger i nettbanken din. Moskeen sender ingen egen kvittering.';
  return 'Kvitteringen din ligger i Vipps.';
}

/**
 * Whether a method can carry a standing monthly gift (CA-78).
 *
 * Vipps can: the member sets up the agreement in Vipps and manages it there.
 * A card can technically be stored for recurring charges, but that means
 * someone holds the card on file, the card expires roughly every three years
 * and the standing gift then silently stops. We are not building a rail that
 * quietly cancels people's sadaqah, so recurring by card is off, and the app
 * says why rather than showing a toggle that does nothing.
 */
function recurringSupported(method, { enabled = true } = {}) {
  // Fast giver (2026-10-31): Vipps Recurring, and only where the mosque's sales unit has the product
  // (MASJID_VIPPS_RECURRING=on; vipps-recurring.enabled). The method rule is unchanged: never a card, never a bank order.
  return method === 'vipps' && enabled !== false;
}
recurringSupported.reason_no = function (method) {
  if (method === 'card')
    return 'Fast månedlig giver settes opp i Vipps. Kort kan brukes til enkeltgaver.';
  /* A standing bank order is a real thing and plenty of members already use
     one — but it lives entirely in THEIR bank. We cannot create it, see it,
     change it or stop it, so offering "fast giver" here against a bank
     transfer would be a button that does nothing. Say where it is actually
     done instead, which is a useful answer rather than a refusal. */
  if (method === 'bank_transfer')
    return 'Fast overføring settes opp i nettbanken din. Moskeen kan ikke endre eller stoppe den for deg.';
  return 'Denne betalingsmåten støtter ikke fast giver.';
};

/**
 * A pending donation that never appeared in the import.
 *
 * After two weeks it is either a payment that failed, or one Solidus recorded
 * under a different member. Both need a human, and the member should not be
 * left wondering.
 */
function reconcile(pending = [], donations = [], now = new Date()) {
  const seen = new Set((donations || []).map(d => String(d.reference || '')));
  return (pending || []).map(p => {
    const days = Math.floor((now - new Date(p.startedAt)) / 86400000);
    if (seen.has(String(p.reference))) return { ...p, state: 'confirmed', days };
    if (days >= 14) {
      /* The method is carried through untouched, because it is the first thing
         whoever chases this will ask — a missing Vipps gift and a missing card
         gift are looked up in two different places at Solidus. It is also the
         only thing about the payment we are allowed to hold. */
      return { ...p, state: 'unresolved', days,
        message_no: 'Vi finner ikke igjen denne gaven. Ta kontakt med moskeen med referansen, så finner de ut av det.',
        showContact: true };
    }
    return { ...p, state: 'waiting', days };
  });
}

/**
 * Recurring giving (CA-78).
 *
 * Set up in Vipps, cancelled in Vipps. The app links out to manage it and
 * says so — showing a "Stopp fast giver" button that only sends the member to
 * Vipps anyway, or worse fails silently, is how somebody keeps paying for
 * months while believing they stopped.
 */
function recurring(agreement, now = new Date()) {
  if (!agreement) {
    return {
      active: false,
      title_no: 'Fast givertjeneste',
      body_no: 'Sett opp et fast månedlig beløp i Vipps. Du kan endre eller stoppe det når som helst.',
      action: { label_no: 'Sett opp i Vipps', external: true },
    };
  }
  /*
   * Fast giver (2026-10-31, vipps-recurring.js): an agreement made IN THE APP carries its Vipps agreement id, and the
   * app's «Stopp» really stops it (POST /me/agreements/:id/stop asks Vipps, and only then marks it stopped) — so that
   * one may be cancelled in the app. A row without the id was set up in Vipps alone (the import era) and still can
   * only be managed there: the button that cannot cancel is still not drawn for it.
   */
  const inApp = Boolean(agreement.vipps_agreement_id);
  return {
    active: agreement.status === 'active',
    amount: agreement.amount,
    interval_no: 'hver måned',
    since: agreement.started_at,
    nextCharge: agreement.next_charge_at || null,
    // The honest sentence.
    manage_no: inApp ? 'Den faste gaven kan stoppes i appen under «Faste gaver», eller i Vipps. Vil du endre beløpet, stopper du den og setter opp en ny.'
      : 'Fast givertjeneste administreres i Vipps. Du kan endre beløp eller stoppe den der.',
    action: { label_no: 'Administrer i Vipps', external: true, url: agreement.manage_url || null },
    stopNote_no: 'Stopper du avtalen i Vipps, slutter trekket fra neste måned.',
    canCancelInApp: inApp,
    agreementId: inApp ? agreement.id || null : null,
  };
}

/**
 * What the donate screen offers. No default amount is pre-selected — a
 * pre-filled 500 kr is a nudge, and nudging a congregation about money is not
 * something this app should do.
 */
function screen(opts = {}) {
  // Never true in the product. CJAS declined the approved-nonprofit route on
  // CA-87, so every method collects outside the app on both platforms.
  const { platform = 'ios', approvedNonprofit = APPROVED_NONPROFIT, lang = 'no',
          cardOffered = CARD_OFFERED, cardEnabled = true } = opts;
  return {
    suggested: SUGGESTED,
    preselected: null,
    allowCustom: true,
    purposes: PURPOSES.map(p => ({ id: p, label_no: PURPOSE_NO[p] })),
    presentation: presentation(platform, approvedNonprofit),
    ...methodsFor({ cardOffered, cardEnabled, lang }),
    // Recurring exists, but only on the method that can actually keep it
    // running. The screen shows the option against Vipps, not against card.
    recurringAvailable: true,
    recurringMethods: METHODS.filter(recurringSupported),
    taxNote_no: 'Gaver over 500 kr i året gir skattefradrag. Moskeen rapporterer beløpet til Skatteetaten.',
    // Method-specific, because "kvitteringen ligger i Vipps" is wrong for
    // half the people reading it once card is switched on.
    receiptNote_no: 'Hvor kvitteringen havner avhenger av betalingsmåten. Giverhistorikken i appen oppdateres når medlemslisten oppdateres.',
    receiptNoteFor: receiptNote,
    lang,
    // No progress bar, no target, no "X andre har gitt i dag". Same rule as
    // giving.js: this is between the member and God.
    showsCampaignProgress: false,
  };
}

/**
 * A Vipps start we could not confirm (the server said vipps_start_unknown, or a status check still says «unknown»).
 * Review 2026-10-08: when the app stops polling, it must NOT drop the giver back on a blank form, where «Gi» starts a
 * second gift. While a start is unresolved the screen keeps its reference, offers «Sjekk status», and does not start
 * another Vipps gift (bank transfer stays open). It resolves only when Vipps says something final, or that the giver
 * has given (received).
 *
 *   unresolvedStart(status)  → the reference to keep, or null
 *   afterPolling(status)     → what the screen offers when polling stops without a final answer
 *   mayStartVipps(unresolved) → whether «Gi» with Vipps may start a new payment now
 */
function unresolvedStart(status) {
  if (!status || typeof status.reference !== 'string' || !status.reference) return null;
  if (status.final === true || status.claim === 'received') return null;
  return status.claim === 'unknown' ? status.reference : null;
}
function afterPolling(status) {
  const ref = unresolvedStart(status);
  if (!ref) return { keep: null, primary: 'done', offerCheck: false };
  return { keep: ref, primary: 'check_status', offerCheck: true, backToBlankForm: false };
}
const mayStartVipps = (unresolved) => !unresolved;

/**
 * The mosque's bank account number for a bank transfer (CA-87), checked.
 *
 * A Norwegian account number is 11 digits: bank register (4), account group
 * (2), account and check digit (5). The last digit is a MOD11 check over the
 * first ten with weights 5,4,3,2,7,6,5,4,3,2: remainder 0 gives check digit 0,
 * remainder 1 means no valid check digit exists (the number is invalid), any
 * other remainder r gives 11 − r.
 *
 * Accepted: the eleven digits as they are, or grouped 4-2-5 with ONE kind of
 * separator (a space or a dot), with ordinary spaces around it. Anything else —
 * letters, a newline, 10 or 12 digits, a wrong check digit, all zeros in the
 * bank register — is null. Returns the formatted `1234.56.78903`, never a
 * repaired or guessed value: a typo that still looks like an account number is
 * how somebody sends money to nobody, so garbage never comes out looking valid.
 *
 * This checks the FORM only. It cannot say the account belongs to the mosque;
 * that is what the account holder's written confirmation (CA-87) is for.
 */
const ACCOUNT_WEIGHTS = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
const ACCOUNT_FORMATTED = /^\d{4}\.\d{2}\.\d{5}$/;
function accountNumber(raw) {
  if (typeof raw !== 'string') return null;
  const m = /^(\d{4})([ .]?)(\d{2})\2(\d{5})$/.exec(raw.replace(/^ +| +$/g, ''));
  if (!m) return null;
  const digits = m[1] + m[3] + m[4];
  if (m[1] === '0000') return null;   // no bank register 0000: an all-zero placeholder is not an account
  let sum = 0;
  for (let i = 0; i < 10; i++) sum += ACCOUNT_WEIGHTS[i] * Number(digits[i]);
  const r = sum % 11;
  if (r === 1) return null;
  const check = r === 0 ? 0 : 11 - r;
  if (check !== Number(digits[10])) return null;
  return `${m[1]}.${m[3]}.${m[4]}`;
}

module.exports = {
  unresolvedStart, afterPolling, mayStartVipps,
  PROVIDERS, METHODS, PURPOSES, PURPOSE_NO, APPROVED_NONPROFIT, CARD_OFFERED, SUGGESTED, MIN_AMOUNT, MAX_AMOUNT,
  presentation, methodsFor, receiptNote, recurringSupported,
  validateAmount, start, onReturn, reconcile, recurring, screen, referencePrefix,
  accountNumber, ACCOUNT_FORMATTED, REFERENCE_SPACE,
};
